import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import upload from "../../../assets/images/CloudUpload.svg";
import DeleteIcon from "../../../assets/images/deleteIconRed.svg";

interface FileUploadProps {
  fileTypeLabel?: string;
  fileUrl?: string | null;
  onChange: (files: File[]) => void;
}

const ExcelCSVFileUpload: React.FC<FileUploadProps> = ({
  fileTypeLabel = "file",
  fileUrl,
  onChange,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(fileUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    setPreviewUrl(fileUrl || null);
  }, [fileUrl]);

  const validateFileType = (file: File) => {
    const allowedTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    return allowedTypes.includes(file.type);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = event.target.files ? Array.from(event.target.files) : [];
    const validFiles = newFiles.filter(validateFileType);

    if (validFiles.length === 0) {
      setError("Only CSV and Excel files are allowed.");
      return;
    }

    setFiles(validFiles);
    setError("");
    setPreviewUrl(URL.createObjectURL(validFiles[0]));
    onChange(validFiles);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(event.dataTransfer.files);
    const validFiles = droppedFiles.filter(validateFileType);

    if (validFiles.length === 0) {
      setError("Only CSV and Excel files are allowed.");
      return;
    }

    setFiles(validFiles);
    setError("");
    setPreviewUrl(URL.createObjectURL(validFiles[0]));
    onChange(validFiles);
  };

  const handleDelete = () => {
    setFiles([]);
    setPreviewUrl(null);
    setError("");
    onChange([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box>
      <Box
        sx={{
          border: "2px dashed #CCCCCC",
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: isDragging ? "#f1f1f1" : "#212121 !important",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "234px",
          transition: "background-color 0.2s",
          position: "relative",
          overflow: "hidden",
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept=".csv, .xls, .xlsx"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        <img src={upload} alt="Upload Icon" />
        <Typography
          variant="h6"
          sx={{ mt: 2, fontSize: "16px", fontWeight: "500" }}
        >
          Drop your {fileTypeLabel} here or{" "}
          <Box
            component="span"
            sx={{
              color: "red",

              cursor: "pointer",
              mx: "4px",
            }}
          >
            browse
          </Box>
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontSize: "14px", mt: 1, color: "#E6E6E6" }}
        >
          Max. File Size: 200MB
        </Typography>
      </Box>

      {error && (
        <Typography color="error" sx={{ mt: 1, fontSize: "14px" }}>
          {error}
        </Typography>
      )}

      {previewUrl && (
        <List sx={{ mt: 2, width: "100%" }}>
          <ListItem sx={{ borderBottom: "1px solid #808080", paddingX: 0 }}>
            <ListItemText
              primary={files.length > 0 ? files[0].name : "Existing File"}
            />
            <IconButton edge="end" onClick={handleDelete}>
              <img src={DeleteIcon} alt="Delete" width={20} height={20} />
            </IconButton>
          </ListItem>
        </List>
      )}
    </Box>
  );
};

export default ExcelCSVFileUpload;
